'use client';

import React, { memo, useState } from 'react';

import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import type { ButtonProps, DialogTitleProps } from '@mui/material';
import { FaRegTrashAlt } from '@react-icons/all-files/fa/FaRegTrashAlt';

import Button from './index';

interface DeleteButtonProps {
  title: string;
  subTitle?: string;
  confirmText?: string;
  deleteItem?: any;
  disabled?: boolean;
  onDelete: (item?: any) => Promise<void>;
  onClose?: () => void;
  children?: any;
  buttonProps?: ButtonProps;
  Icon?: React.ElementType;
  renderContent?: React.ReactNode;
  fullWidthButton?: boolean;
  titleProps?: DialogTitleProps;
  variant?: 'regular' | 'icon';
  buttonText?: string;
}

export const DeleteButton = memo<DeleteButtonProps>(
  ({
    title,
    subTitle,
    onDelete,
    onClose,
    deleteItem,
    confirmText = 'Excluir',
    children,
    buttonProps,
    disabled = false,
    Icon = FaRegTrashAlt,
    renderContent = undefined,
    fullWidthButton = false,
    titleProps,
    variant = 'icon',
    buttonText = 'Excluir',
  }) => {
    const [loading, setLoading] = useState(false);
    const [open, setOpen] = useState(false);

    const handleClose = () => {
      setOpen(false);
      if (onClose) {
        onClose();
      }
    };

    const handleOpen = () => {
      if (disabled) return;

      setOpen(true);
    };

    const onConfirmDelete = async () => {
      setLoading(true);
      await onDelete(deleteItem);
      setLoading(false);
      handleClose();
    };

    return (
      <>
        <Dialog open={open} onClose={handleClose} maxWidth="xs">
          <DialogTitle sx={{ pb: 1, ...titleProps?.sx }} {...titleProps}>
            {title}
          </DialogTitle>

          {renderContent && <DialogContent>{renderContent}</DialogContent>}

          {subTitle && !renderContent && (
            <DialogContent>
              <Typography variant="body2">{subTitle}</Typography>
            </DialogContent>
          )}

          <DialogActions>
            <Button
              variant="outlined"
              color="inherit"
              disabled={loading}
              onClick={handleClose}
              fullWidth={fullWidthButton}
            >
              Cancelar
            </Button>
            <Button
              id="confirm-delete-button"
              loading={loading}
              variant="contained"
              color="error"
              onClick={onConfirmDelete}
              fullWidth={fullWidthButton}
            >
              {confirmText}
            </Button>
          </DialogActions>
        </Dialog>

        {!!children ? (
          <div
            onClick={handleOpen}
            style={{
              display: 'inline-block',
              cursor: disabled ? 'auto' : 'pointer',
            }}
          >
            {children}
          </div>
        ) : (
          <>
            {variant === 'regular' ? (
              <Button
                id="delete-button"
                disabled={disabled}
                variant="outlined"
                color="error"
                {...buttonProps}
                sx={{
                  ...buttonProps?.sx,
                }}
                onClick={handleOpen}
              >
                {buttonText}
              </Button>
            ) : (
              <IconButton
                id="delete-button"
                disabled={disabled}
                {...buttonProps}
                sx={{
                  border: '1px solid #E3E3E3',
                  borderRadius: 1,
                  ...buttonProps?.sx,
                }}
                onClick={handleOpen}
              >
                <Icon size={16} />
              </IconButton>
            )}
          </>
        )}
      </>
    );
  }
);
